/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import MockAdapter from 'axios-mock-adapter';
import { makeMockResponse } from '../mockUtils';
import { delayedResponse } from '../companies/companiesMock';
import {
    storeDetailsFactory,
    storeListItemDtoFactory,
    storeCashBoxesFactory,
    storeCashBoxesStatsFactory,
    turnoversByCashBoxFactory,
    storeDetailsForMapFactory,
    storeOnMapFactory,
    coordinatesHistoryItemFactory,
    inspectionsFactory,
    aiClassificationsHistoryItemFactory,
    storeCashBoxesIntegrationFactory,
    storeIntegrationFactory,
} from './storesFactories';
import {
    StoreDetailsDto,
    StoreCashboxesListDto,
    StoreDetailsForMap,
    UpdatedStoreDetailsDto,
    StoreCoordinatesHistoryDto,
    StoreInspectionsHistoryDto,
    StoreInspectionDto,
    InspectionStatus,
    StoresListDto,
    StoresForMap,
    StorePoint,
    StoreCashBoxesIntegrationData,
    StoreIntegrationsData,
} from './storesClientTypings';
import {
    RegistryTurnoversByPeriodDto,
    RegistryTurnoversByCashBoxesDto,
    AiClassificationsHistoryDto,
} from '../apiCommon.typings';
import { TurnoversByPeriodFactory } from '../cashBoxes/cashBoxesFactories';
import moment from 'moment';
import { CashBoxesStats } from '../cashBoxes/cashBoxesClient.typings';

const STORES_URL: RegExp = /\/stores$/;
const STORES_FOR_MAP_URL: RegExp = /\/map\/stores/;
const STORES_DETAILS_FOR_MAP_URL: RegExp = /map\/stores\/\d+$/;
const STORES_DETAILS_URL: RegExp = /\/stores\/(\d+)$/;
const STORES_CASHBOXES_URL: RegExp = /\/stores\/(\d+)\/cashboxes$/;
const STORE_CASHBOXES_STATS: RegExp = /\/stores\/(\d+)\/cashboxes\/stats/;
const COORDINATES_HISTORY: RegExp = /\/stores\/(\d+)\/coordinatesHistory/;
const INSPECTIONS: RegExp = /\/stores\/(\d+)\/inspections/;
const STORES_GET_TURNOVERS_BY_PERIOD: RegExp = /\/stores\/\d+\/turnovers/;
const STORES_GET_TURNOVERS_BY_CASHBOXES: RegExp = /\/stores\/\d+\/cashboxes\/turnovers/;
const AI_CLASSIFICATIONS_HISTORY = /\/stores\/\d+\/classificationsHistory$/;
const STORE_CASHBOXES_INTEGRATAION = /\/stores\/(\d+)\/cashboxes\/integrations$/;
const STORES_INTEGRATION = /\/stores\/(\d+)\/integration$/;

export function addStoreMockApi(mockAdapter: MockAdapter): void {
    const storesBackend = new StoresForMapBackendStore({
        total: 60000,
    });

    mockAdapter.onPost(STORES_FOR_MAP_URL).reply((config) => {
        const params = JSON.parse(config.data);
        const storesPoints = storesBackend.getStores();

        let data: StoresForMap = {
            clusters: storesClusters,
            points: storesPoints,
        };

        if (params.hasOwnProperty('taxNumber') && data.points.find((store) => store.taxNumber === params.taxNumber)) {
            data = {
                clusters: [],
                points: [data.points.find((store) => store.taxNumber === params.taxNumber)],
            };
        }

        if (
            params.hasOwnProperty('taxNumber') &&
            data.points.find((store) => store.taxNumber === params.taxNumber) === undefined
        ) {
            data = {
                clusters: [],
                points: [storesBackend.getStores()[0]],
            };
        }

        return delayedResponse(makeMockResponse(200, data, config), Math.random() * 100);
    });

    mockAdapter.onGet(STORES_DETAILS_FOR_MAP_URL).reply((config) => {
        const data: StoreDetailsForMap = storeDetailsForMapFactory.item();

        return delayedResponse(makeMockResponse(200, data, config), Math.random() * 200);
    });

    mockAdapter.onPost(STORES_URL).reply((config) => {
        const body = JSON.parse(config.data);
        const data: StoresListDto = {
            afterKey: '234318932',
            stores: storeListItemDtoFactory.list({ count: body.limit }),
        };

        return delayedResponse(makeMockResponse(200, data, config), Math.random() * 200);
    });

    mockAdapter.onGet(STORES_GET_TURNOVERS_BY_CASHBOXES).reply((config) => {
        const data: RegistryTurnoversByCashBoxesDto = {
            count: 100,
            turnovers: turnoversByCashBoxFactory.list({ count: config.params.limit }),
        };

        return delayedResponse(makeMockResponse(200, data, config), Math.random() * 1500);
    });

    mockAdapter.onGet(STORES_GET_TURNOVERS_BY_PERIOD).reply((config) => {
        const data: RegistryTurnoversByPeriodDto[] = TurnoversByPeriodFactory.list({ count: 12 });
        TurnoversByPeriodFactory.resetCount();

        return delayedResponse(makeMockResponse(200, data, config), Math.random() * 1500);
    });

    mockAdapter.onGet(STORE_CASHBOXES_STATS).reply((config) => {
        const data: CashBoxesStats = storeCashBoxesStatsFactory.item();

        return delayedResponse(makeMockResponse(200, data, config), Math.random() * 300);
    });

    mockAdapter.onGet(STORES_CASHBOXES_URL).reply((config) => {
        const data: StoreCashboxesListDto = {
            count: 18,
            cashboxes: storeCashBoxesFactory.list({ count: config.params.limit }),
        };

        return delayedResponse(makeMockResponse(200, data, config), Math.random() * 300);
    });

    mockAdapter.onGet(STORES_DETAILS_URL).reply((config) => {
        const data: StoreDetailsDto = storeDetailsFactory.item();

        return delayedResponse(makeMockResponse(200, data, config), Math.random() * 200);
    });

    mockAdapter.onPatch(STORES_DETAILS_URL).reply((config) => {
        const data: Partial<UpdatedStoreDetailsDto> = {};

        const body = JSON.parse(config.data);
        data.lat = body.lat;
        data.lng = body.lng;

        return delayedResponse(makeMockResponse(200, data, config), 300);
    });

    mockAdapter.onGet(COORDINATES_HISTORY).reply((config) => {
        const data: StoreCoordinatesHistoryDto = {
            count: 5,
            items: coordinatesHistoryItemFactory.list({ count: 5 }),
        };

        return delayedResponse(makeMockResponse(200, data, config), 300);
    });

    mockAdapter.onPost(INSPECTIONS).reply((config) => {
        const body = JSON.parse(config.data);
        const data: StoreInspectionDto = inspectionsFactory.item();
        data.status = InspectionStatus.OPEN;
        data.initiatorComment = body.initiatorComment;
        data.insertedAtUtc = moment().utc().format();

        return delayedResponse(makeMockResponse(200, data, config), 300);
    });

    mockAdapter.onDelete(INSPECTIONS).reply((config) => {
        return delayedResponse(makeMockResponse(200, {}, config), 300);
    });

    mockAdapter.onGet(INSPECTIONS).reply((config) => {
        const data: StoreInspectionsHistoryDto = {
            count: 5,
            items: inspectionsFactory.list({ count: 5 }),
        };

        return delayedResponse(makeMockResponse(200, data, config), 300);
    });

    mockAdapter.onPatch(INSPECTIONS).reply((config) => {
        const body = JSON.parse(config.data);
        const data: StoreInspectionDto = inspectionsFactory.item();

        data.inspectionId = config.url.split('/')[4];
        data.status = body.status;

        if (body.status === InspectionStatus.CLOSED) {
            data.productName = body.productName;
            data.result = body.result;
            data.resultComment = body.resultComment;
            data.inspectedAtUtc = body.inspectedAtUtc;
        }

        return delayedResponse(makeMockResponse(200, data, config), 300);
    });

    mockAdapter.onGet(AI_CLASSIFICATIONS_HISTORY).reply((config) => {
        const data: AiClassificationsHistoryDto = {
            count: config.params.limit * 3,
            items: aiClassificationsHistoryItemFactory.list({ count: config.params.limit }),
        };

        return delayedResponse(makeMockResponse(200, data, config), 300);
    });

    mockAdapter.onGet(STORE_CASHBOXES_INTEGRATAION).reply((config) => {
        const data: StoreCashBoxesIntegrationData = {
            count: config.params.limit * 3,
            cashboxes: storeCashBoxesIntegrationFactory.list({ count: config.params.limit }),
        };

        return delayedResponse(makeMockResponse(200, data, config), 300);
    });

    mockAdapter.onGet(STORES_INTEGRATION).reply((config) => {
        const data: StoreIntegrationsData = storeIntegrationFactory.item();
        return delayedResponse(makeMockResponse(200, data, config), 300);
    });

    mockAdapter.onPost(STORES_INTEGRATION).reply((config) => {
        const data: StoreIntegrationsData = storeIntegrationFactory.item();
        const newData = {
            ...data,
            ...JSON.parse(config.data),
        };

        return delayedResponse(makeMockResponse(200, newData, config), 300);
    });
}

export class StoresForMapBackendStore {
    private stores: StorePoint[] = [];

    constructor(options: { total: number }) {
        for (let index = 0; index < options.total; index++) {
            const southWest = { lat: 40.384212768155045, lng: 49.83140945434571 };
            const northEast = { lat: 40.43427422075976, lng: 49.902734756469734 };

            const lngSpan = northEast.lng - southWest.lng;
            const latSpan = northEast.lat - southWest.lat;

            this.createStore(southWest.lat + latSpan * Math.random(), southWest.lng + lngSpan * Math.random());
        }
    }

    public getStores() {
        return this.stores;
    }

    private createStore(lat: number, lng: number): void {
        const store = storeOnMapFactory.item();

        store.lat = lat;
        store.lon = lng;

        this.stores.push(store);
    }
}

const storesClusters = [
    {
        count: 100,
        geoHash: 'tp5myn',
    },
    {
        count: 140,
        geoHash: 'tp5myx',
    },
    {
        count: 120,
        geoHash: 'tp5myg',
    },
    {
        count: 99,
        geoHash: 'tp6myx',
    },
    {
        count: 160,
        geoHash: 'tp9myx',
    },
];
